import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import LandingPage from './LandingPage';
import downloadIcon from './assets/E.png';

const APPS_SCRIPT_URL = process.env.REACT_APP_APPS_SCRIPT_URL || '';
const AUTO_REFRESH_INTERVAL = 3000;
const MAX_LOGIN_ATTEMPTS = 5;
const BLOCK_DURATION_HOURS = 5;
const BLOCK_DURATION_MS = BLOCK_DURATION_HOURS * 60 * 60 * 1000;
const SAFE_ID_PATTERN = /^[a-zA-Z0-9а-яА-ЯёЁ_-]+$/;
const MAX_ID_LENGTH = 50;

function App() {
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [showCubeLoader, setShowCubeLoader] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [remainingBlockTime, setRemainingBlockTime] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const fileInputRef = useRef(null);
  const autoRefreshIntervalRef = useRef(null);
  const lastHistoryLengthRef = useRef(0);
  const dropZoneRef = useRef(null);
  const commentInputRef = useRef(null);
  const blockTimerRef = useRef(null);
  const historyListRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sanitizeAndValidateId = (input) => {
    if (!input) return '';
    
    let cleaned = input.trim();
    
    if (cleaned.length > MAX_ID_LENGTH) {
      cleaned = cleaned.substring(0, MAX_ID_LENGTH);
    }
    
    cleaned = cleaned
      .replace(/<script/gi, '')
      .replace(/<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/onload=/gi, '')
      .replace(/onerror=/gi, '')
      .replace(/alert\(/gi, '')
      .replace(/eval\(/gi, '')
      .replace(/document\./gi, '')
      .replace(/window\./gi, '')
      .replace(/--/g, '')
      .replace(/`/g, '')
      .replace(/\\/g, '')
      .replace(/\.\.\//g, '')
      .replace(/&/g, '')
      .replace(/</g, '')
      .replace(/>/g, '')
      .replace(/"/g, '')
      .replace(/'/g, '');
    
    if (!SAFE_ID_PATTERN.test(cleaned)) {
      return null;
    }
    
    return cleaned;
  };

  const handleUserIdChange = (e) => {
    let rawValue = e.target.value;
    
    if (rawValue.length > MAX_ID_LENGTH) {
      rawValue = rawValue.substring(0, MAX_ID_LENGTH);
    }
    
    let safeValue = rawValue
      .replace(/[<>]/g, '')
      .replace(/[&]/g, '')
      .replace(/["']/g, '')
      .replace(/[`]/g, '')
      .replace(/[\\]/g, '');
    
    setUserId(safeValue);
    if (loginError) setLoginError('');
  };

  useEffect(() => {
    const savedId = localStorage.getItem('userId');
    const savedName = localStorage.getItem('userName');
    
    if (savedId && savedName) {
      setUserId(savedId);
      setUserName(savedName);
      setLoggedIn(true);
    }
    
    const savedAttempts = localStorage.getItem('loginAttempts');
    const savedBlockedUntil = localStorage.getItem('blockedUntil');
    
    if (savedAttempts) {
      setLoginAttempts(parseInt(savedAttempts));
    }
    
    if (savedBlockedUntil) {
      const blockEndTime = parseInt(savedBlockedUntil);
      const now = Date.now();
      
      if (now < blockEndTime) {
        setIsBlocked(true);
        setBlockedUntil(blockEndTime);
        startBlockTimer(blockEndTime);
      } else {
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('blockedUntil');
        setLoginAttempts(0);
      }
    }
    
    setShowLanding(true);
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      if (blockTimerRef.current) {
        clearInterval(blockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loggedIn && !showLanding && userId) {
      loadHistory(userId);
      startAutoRefresh(userId);
    } else if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
    
    return () => {
      if (autoRefreshIntervalRef.current && showLanding) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [loggedIn, showLanding, userId]);

  useEffect(() => {
    if (!loggedIn || showLanding || !userId) return;
    
    let focusInterval = null;
    
    const handleFocus = () => {
      loadHistory(userId, true);
      
      if (focusInterval) clearInterval(focusInterval);
      focusInterval = setInterval(() => {
        loadHistory(userId, true);
      }, 2000);
      
      setTimeout(() => {
        if (focusInterval) clearInterval(focusInterval);
        focusInterval = null;
      }, 10000);
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      if (focusInterval) clearInterval(focusInterval);
    };
  }, [loggedIn, showLanding, userId]);

  const startBlockTimer = (blockEndTime) => {
    if (blockTimerRef.current) {
      clearInterval(blockTimerRef.current);
    }
    
    const updateRemainingTime = () => {
      const now = Date.now();
      const remaining = Math.max(0, blockEndTime - now);
      setRemainingBlockTime(remaining);
      
      if (remaining <= 0) {
        clearInterval(blockTimerRef.current);
        setIsBlocked(false);
        setBlockedUntil(null);
        setLoginAttempts(0);
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('blockedUntil');
        setLoginError('');
      }
    };
    
    updateRemainingTime();
    blockTimerRef.current = setInterval(updateRemainingTime, 1000);
  };

  const formatBlockTime = (ms) => {
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м ${seconds}с`;
    } else if (minutes > 0) {
      return `${minutes}м ${seconds}с`;
    } else {
      return `${seconds}с`;
    }
  };

  const checkId = async () => {
    setLoginError('');
    
    if (isBlocked) {
      const remainingTime = formatBlockTime(remainingBlockTime);
      setLoginError(`Доступ заблокирован на ${BLOCK_DURATION_HOURS} часов. Осталось: ${remainingTime}`);
      return;
    }
    
    if (!APPS_SCRIPT_URL) {
      setErrorMessage('Ошибка: URL сервера не настроен');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    if (!userId) {
      setErrorMessage('Пожалуйста, введите ID');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    const sanitizedId = sanitizeAndValidateId(userId);
    
    if (sanitizedId === null) {
      setErrorMessage('ID содержит недопустимые символы');
      setTimeout(() => setErrorMessage(''), 5000);
      
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('loginAttempts', newAttempts);
      
      if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
        const blockEndTime = Date.now() + BLOCK_DURATION_MS;
        setIsBlocked(true);
        setBlockedUntil(blockEndTime);
        localStorage.setItem('blockedUntil', blockEndTime);
        startBlockTimer(blockEndTime);
        setLoginError(`Неверный ID. Доступ заблокирован на ${BLOCK_DURATION_HOURS} часов.`);
      } else {
        const remainingAttempts = MAX_LOGIN_ATTEMPTS - newAttempts;
        setLoginError(`Неверный ID. Осталось попыток: ${remainingAttempts}`);
      }
      return;
    }
    
    if (sanitizedId.length === 0) {
      setErrorMessage('ID не может быть пустым');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    setShowCubeLoader(true);

    try {
      const url = `${APPS_SCRIPT_URL}?action=checkId&id=${encodeURIComponent(sanitizedId)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (data.valid === true) {
        setLoginAttempts(0);
        localStorage.removeItem('loginAttempts');
        localStorage.removeItem('blockedUntil');
        
        const userNameFromServer = data.name || 'Пользователь';
        localStorage.setItem('userId', sanitizedId);
        localStorage.setItem('userName', userNameFromServer);
        setUserId(sanitizedId);
        setUserName(userNameFromServer);
        setLoggedIn(true);
        setShowLanding(false);
        await loadHistory(sanitizedId);
        startAutoRefresh(sanitizedId);
      } else {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        localStorage.setItem('loginAttempts', newAttempts);
        
        const remainingAttempts = MAX_LOGIN_ATTEMPTS - newAttempts;
        
        if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
          const blockEndTime = Date.now() + BLOCK_DURATION_MS;
          setIsBlocked(true);
          setBlockedUntil(blockEndTime);
          localStorage.setItem('blockedUntil', blockEndTime);
          startBlockTimer(blockEndTime);
          setLoginError(`Неверный ID. Доступ заблокирован на ${BLOCK_DURATION_HOURS} часов.`);
        } else {
          setLoginError(`Неверный ID. Осталось попыток: ${remainingAttempts}`);
        }
        
        setTimeout(() => setLoginError(''), 5000);
      }
    } catch(error) {
      console.error('Check ID error:', error);
      setErrorMessage('Ошибка соединения: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setShowCubeLoader(false);
    }
  };

  const loadHistory = async (id, isAutoRefresh = false) => {
    if (!APPS_SCRIPT_URL) {
      console.log('APPS_SCRIPT_URL is not configured');
      return;
    }
    
    try {
      const url = `${APPS_SCRIPT_URL}?action=getHistory&id=${encodeURIComponent(id)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      if (!data.history || !Array.isArray(data.history)) {
        setHistory([]);
        return [];
      }
      
      const formattedHistory = [];
      
      for (const item of data.history) {
        let commentText = item.comment || '';
        if (typeof commentText !== 'string') {
          commentText = String(commentText);
        }
        
        commentText = commentText.replace(/\+/g, ' ');
        
        const isAdminMessage = item.isAdmin === true || item.name === 'Администратор';
        
        let senderName = item.name;
        if (isAdminMessage) {
          senderName = 'Администратор';
        } else if (!senderName || senderName === 'Администратор') {
          senderName = userName;
        }
        
        formattedHistory.push({
          date: item.date,
          comment: commentText,
          name: senderName,
          isAdmin: isAdminMessage,
          files: item.files && Array.isArray(item.files) ? item.files.map(f => ({
            filename: f.filename,
            url: f.url,
            fileId: f.fileId,
            viewUrl: f.viewUrl,
            date: f.date || item.date
          })) : []
        });
      }
      
      const newLength = formattedHistory.length;
      const wasNewMessage = !isAutoRefresh && lastHistoryLengthRef.current > 0 && newLength > lastHistoryLengthRef.current;
      
      if (wasNewMessage) {
        playNotificationSound();
      }
      
      lastHistoryLengthRef.current = newLength;
      
      setHistory(formattedHistory);
      return formattedHistory;
      
    } catch(error) {
      console.error('Error loading history:', error);
      setHistory([]);
      return [];
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch(e) {
      console.log('Sound error:', e);
    }
  };

  const uploadFiles = async () => {
    if (files.length === 0 && (!comment || comment.trim() === '')) {
      setErrorMessage('Введите сообщение или выберите файлы для отправки');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    
    const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
    
    if (totalSize > MAX_SIZE_BYTES) {
      setWarningMessage(`Общий размер файлов превышает ${MAX_SIZE_MB} МБ`);
      setTimeout(() => setWarningMessage(''), 5000);
      return;
    }

    if (!APPS_SCRIPT_URL) {
      setErrorMessage('Ошибка: URL сервера не настроен');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    setUploading(true);
    setLoading(true);
    
    const uploadBtn = document.querySelector('.upload-btn');
    if (uploadBtn) uploadBtn.classList.add('loading');
    
    try {
      let filesData = [];
      
      if (files.length > 0) {
        for (const file of files) {
          const reader = new FileReader();
          const base64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          
          filesData.push({
            filename: file.name,
            data: base64,
            mimeType: file.type
          });
        }
      }
      
      const params = new URLSearchParams();
      params.append('action', 'uploadMultipleBase64');
      params.append('id', userId);
      params.append('name', userName);
      params.append('comment', comment);
      params.append('files', JSON.stringify(filesData));
      
      const response = await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString()
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (uploadBtn) {
          uploadBtn.classList.remove('loading');
          uploadBtn.classList.add('upload-success');
          setTimeout(() => uploadBtn.classList.remove('upload-success'), 500);
        }
        
        if (files.length > 0 && comment.trim()) {
          setSuccessMessage(`Отправлено ${data.fileCount || files.length} файл(ов) с комментарием`);
        } else if (files.length > 0) {
          setSuccessMessage(`Отправлено ${data.fileCount || files.length} файл(ов)`);
        } else if (comment.trim()) {
          setSuccessMessage('Сообщение отправлено');
        }
        
        setTimeout(() => setSuccessMessage(''), 3000);
        
        setComment('');
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        shouldAutoScrollRef.current = true;
        await loadHistory(userId);
        
        setTimeout(async () => {
          await loadHistory(userId);
        }, 500);
        
      } else {
        setErrorMessage('Ошибка отправки: ' + (data.error || 'Неизвестная ошибка'));
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch(error) {
      console.error('Upload error:', error);
      setErrorMessage('Ошибка отправки: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    }
    setUploading(false);
    setLoading(false);
    if (uploadBtn) uploadBtn.classList.remove('loading');
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0 || !bytes) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename) => {
    if (!filename || typeof filename !== 'string') return 'FILE';
    
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      'pdf': 'PDF', 'zip': 'ZIP', 'rar': 'RAR', '7z': '7Z',
      'html': 'HTML', 'htm': 'HTML', 'txt': 'TXT', 'json': 'JSON',
      'stl': 'STL', 'obj': 'OBJ', '3mf': '3MF', 'constructioninfo': 'INFO'
    };
    return icons[ext] || 'FILE';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch(e) {
      return '';
    }
  };

  const formatDateForDivider = (dateStr) => {
    if (!dateStr) return 'Дата неизвестна';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Сегодня';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Вчера';
      } else {
        return date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }
    } catch(e) {
      return dateStr;
    }
  };

  const addFiles = useCallback((newFiles) => {
    const validFiles = [];
    const invalidFiles = [];
    
    const allowedExtensions = ['.constructioninfo', '.html', '.htm', '.zip', '.rar', '.stl', '.obj', '.3mf', '.pdf', '.txt', '.json'];
    
    for (const file of newFiles) {
      const fileName = file.name;
      const fileExt = '.' + fileName.split('.').pop().toLowerCase();
      if (allowedExtensions.includes(fileExt)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(fileName);
      }
    }
    
    if (invalidFiles.length > 0) {
      setErrorMessage(`Неподдерживаемые типы файлов: ${invalidFiles.join(', ')}`);
      setTimeout(() => setErrorMessage(''), 5000);
    }
    
    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  }, []);

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      const activeElement = document.activeElement;
      if (activeElement !== commentInputRef.current) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;
      
      const pastedFiles = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      
      if (pastedFiles.length > 0) {
        e.preventDefault();
        addFiles(pastedFiles);
        
        if (commentInputRef.current) {
          commentInputRef.current.classList.add('paste-flash');
          setTimeout(() => {
            if (commentInputRef.current) {
              commentInputRef.current.classList.remove('paste-flash');
            }
          }, 300);
        }
      }
    };
    
    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [addFiles]);

  const startAutoRefresh = (id) => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    
    autoRefreshIntervalRef.current = setInterval(async () => {
      if (id && loggedIn && !showLanding) {
        try {
          await loadHistory(id, true);
        } catch (error) {
          console.error('Auto-refresh error:', error);
        }
      }
    }, AUTO_REFRESH_INTERVAL);
  };

  const handleStartUpload = () => {
    if (loggedIn) {
      setShowLanding(false);
      loadHistory(userId);
      startAutoRefresh(userId);
    } else {
      setShowLanding(false);
    }
  };

  const handleBackToLanding = () => {
    setShowLanding(true);
  };

  const handleGoToLanding = () => {
    setShowLanding(true);
  };

  const handleBackToUpload = () => {
    setShowLanding(false);
    if (loggedIn && userId) {
      loadHistory(userId);
      startAutoRefresh(userId);
    }
  };

  const groupMessagesByDay = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = new Date(message.date);
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    return groups;
  };

  const downloadFile = async (file) => {
    if (!file.url) {
      setErrorMessage('Ссылка на файл отсутствует');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }
    
    try {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch(error) {
      console.error('Download error:', error);
      setErrorMessage('Ошибка скачивания: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const getTotalSize = () => {
    return files.reduce((sum, f) => sum + (f.size || 0), 0);
  };

  const isOverLimit = () => {
    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    return getTotalSize() > MAX_SIZE_BYTES;
  };

  const CubeLoader = () => (
    <div className="cube-loader-overlay">
      <div className="cube">
        <div className="side"></div>
        <div className="side"></div>
        <div className="side"></div>
        <div className="side"></div>
        <div className="side"></div>
        <div className="side"></div>
      </div>
    </div>
  );

  if (showCubeLoader) {
    return <CubeLoader />;
  }

  if (showLanding) {
    return <LandingPage 
      onStart={handleStartUpload} 
      isLoggedIn={loggedIn} 
      userName={userName} 
      onBackToUpload={handleBackToUpload}
    />;
  }

  if (!loggedIn) {
    return (
      <div className="login">
        <button onClick={handleBackToLanding} className="back-to-landing-btn">← На главную</button>
        <div className="login-header"><h1>Вход в систему</h1></div>
        <div className="login-content">
          <input 
            type="text" 
            placeholder="Введите ваш ID" 
            value={userId} 
            onChange={handleUserIdChange}
            onKeyPress={(e) => e.key === 'Enter' && !isBlocked && checkId()}
            disabled={isBlocked}
            maxLength={MAX_ID_LENGTH}
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
          {loginError && <div className="login-error-message">{loginError}</div>}
          <button onClick={checkId} disabled={isBlocked}>
            {isBlocked ? `Заблокировано (${formatBlockTime(remainingBlockTime)})` : 'Войти'}
          </button>
          <div className="login-hint">
            <small>Введите ID, выданный администратором</small>
          </div>
        </div>
      </div>
    );
  }

  const groupedMessages = groupMessagesByDay(history);

  return (
    <div className="app">
      <div className="app-header">
        <h1>Передача файлов: {userName || 'Пользователь'}</h1>
        <div className={`header-buttons ${isMobile ? 'mobile-header-buttons' : ''}`}>
          <button onClick={handleGoToLanding} className="home-btn">На главную</button>
          <button 
            onClick={() => {
              localStorage.removeItem('userId');
              localStorage.removeItem('userName');
              if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
              setLoggedIn(false);
              setUserId('');
              setUserName('');
              setShowLanding(true);
              setHistory([]);
            }}
            className="logout-btn"
          >Выйти</button>
        </div>
      </div>
      
      {successMessage && <div className="success-notification">{successMessage}</div>}
      {warningMessage && <div className="warning-notification">{warningMessage}</div>}
      {errorMessage && <div className="warning-notification">{errorMessage}</div>}
      
      <div className="app-content">
        <div className="upload-area">
          <div 
            ref={dropZoneRef}
            className={`drop-zone ${isDragOver ? 'drag-over' : ''} ${isMobile ? 'mobile-drop-zone' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              {isMobile ? (
                <div className="mobile-plus-icon">
                  <span className="mobile-upload-text">Загрузить файлы</span>
                </div>
              ) : (
                <>
                  <div className="drop-zone-text">
                    {isDragOver ? 'Отпустите файлы для загрузки' : 'Перетащите файлы сюда или кликните для выбора'}
                  </div>
                  <div className="drop-zone-hint">
                    Поддерживаемые форматы: PDF, ZIP, RAR, STL, OBJ, 3MF, HTML, TXT, JSON (максимум 30 МБ)
                  </div>
                </>
              )}
            </div>
            <input 
              ref={fileInputRef}
              type="file" 
              multiple
              accept=".constructioninfo,.html,.htm,.zip,.rar,.stl,.obj,.3mf,.pdf,.txt,.json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
          
          {files.length > 0 && (
            <div className="file-list-container">
              <div className="file-list-header">
                <h3>Выбрано файлов: {files.length}</h3>
                <div className="file-list-stats">
                  Общий размер: {formatFileSize(getTotalSize())} / 30 МБ
                  {isOverLimit() && <span style={{ color: '#ff9800', marginLeft: '8px' }}>Превышение лимита</span>}
                </div>
              </div>
              <div className="file-list">
                {files.map((file, idx) => (
                  <div key={idx} className="file-item">
                    <div className="file-item-info">
                      <span className="file-icon">[{getFileIcon(file.name)}]</span>
                      <div className="file-details">
                        <div className="file-name">{file.name || 'Без имени'}</div>
                        <div className="file-meta">
                          <span className="file-size">{formatFileSize(file.size)}</span>
                          <span className="file-type">{file.type || 'application/octet-stream'}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeFile(idx)} className="remove-btn" title="Удалить">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <textarea 
            ref={commentInputRef}
            placeholder="Комментарий к файлам или просто отправьте текстовое сообщение" 
            value={comment} 
            onChange={(e) => setComment(e.target.value)} 
            rows={isMobile ? "2" : "3"}
            className="comment-input"
          />
          
          <button 
            onClick={uploadFiles} 
            disabled={loading || (files.length === 0 && (!comment || comment.trim() === ''))}
            className={`upload-btn ${isOverLimit() && files.length > 0 ? 'over-limit-btn' : ''}`}
          >
            {loading ? 'Отправка...' : files.length > 0 ? `Отправить файлы (${files.length})` : 'Отправить сообщение'}
          </button>
          
          {isOverLimit() && files.length > 0 && (
            <div className="limit-warning-text">Невозможно отправить. Общий размер файлов превышает 30 МБ</div>
          )}
        </div>

        <div className="history">
          <h2>История сообщений</h2>
          {history.length === 0 ? (
            <div className="empty-history"><p>Нет сообщений</p></div>
          ) : (
            <div className="history-list" ref={historyListRef}>
              {Object.entries(groupedMessages).map(([dateKey, messages]) => (
                <React.Fragment key={dateKey}>
                  <div className="date-divider">
                    <span className="date-divider-text">{formatDateForDivider(dateKey)}</span>
                  </div>
                  {messages.map((group, idx) => (
                    <div key={idx} className={`message-wrapper ${group.isAdmin ? 'admin-message' : 'user-message'}`}>
                      <div className="message-bubble">
                        <div className="message-header">
                          <span className="message-sender">{group.isAdmin ? 'Администратор' : (group.name || userName)}</span>
                          <span className="message-time">{formatTime(group.date)}</span>
                        </div>
                        
                        {group.comment && typeof group.comment === 'string' && group.comment.trim() !== '' && (
                          <div className="message-comment">{group.comment}</div>
                        )}
                        
                        {group.files && group.files.length > 0 && (
                          <div className="message-files">
                            <div className="files-label">Файлы ({group.files.length})</div>
                            <div className="files-list">
                              {group.files.map((file, fileIdx) => (
                                <div key={fileIdx} className="message-file-item">
                                  <span className="file-icon-small">[{getFileIcon(file.filename)}]</span>
                                  <span className="file-name-small" title={file.filename}>
                                    {file.filename.length > 40 ? file.filename.substring(0, 37) + '...' : file.filename}
                                  </span>
                                  <button onClick={() => downloadFile(file)} className="file-download-btn" title="Скачать">
                                    <img src={downloadIcon} alt="Скачать" style={{ width: '16px', height: '16px' }} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;